import React, { useEffect, useRef, useState } from "react";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { useTranslation } from "react-i18next";
import MediaGallery from "../../Components/MediaGallery";
import { taskRequest } from "./api";

export default function TaskMediaFiles({ taskId, archived, busy, run, onChanged }) {
  const { t } = useTranslation();
  const input = useRef();
  const [attachments, setAttachments] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (!taskId) return;
    let active = true;
    setLoading(true);
    setError("");
    taskRequest("get_task_media_files", { taskId })
      .then((result) => { if (active) setAttachments(result.attachments); })
      .catch((err) => { if (active) setError(err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [taskId, refresh]);

  const mutate = (operation) => run(async () => {
    try {
      await operation();
    } finally {
      // A batch can succeed partially. Always refresh files and activity, while
      // leaving unsaved task fields and their conflict-detection revision alone.
      setRefresh((value) => value + 1);
      await onChanged();
    }
  });
  const upload = () => mutate(async () => {
    const data = new FormData();
    data.append("taskId", taskId);
    files.forEach((file) => data.append("files[]", file));
    await taskRequest("upload_task_media_files", data);
    setFiles([]);
    input.current.value = "";
  });
  const remove = () => {
    const filename = deleting;
    // Show request errors in the task dialog instead of behind this modal.
    setDeleting(null);
    return mutate(() => taskRequest("delete_task_media_file", { taskId, filename }));
  };

  return (
    <section aria-label={t("tasks.mediaFiles")}>
      <Typography variant="h6">{t("tasks.mediaFiles")}</Typography>
      {!taskId ? <Typography color="textSecondary">{t("tasks.saveForMedia")}</Typography> : <>
        {error && <Alert severity="error" action={<Button onClick={() => setRefresh((value) => value + 1)}>{t("mediaPreview.retry")}</Button>}>{error}</Alert>}
        {loading && <CircularProgress size={24} />}
        {!loading && !error && !attachments.length && <Typography color="textSecondary">{t("tasks.noMedia")}</Typography>}
        <MediaGallery attachments={attachments} resource={{ type: "task", id: taskId }}
          onDelete={!archived && !busy ? setDeleting : undefined} />
        {!archived && <div style={{ margin: "16px 0" }}>
          <input ref={input} type="file" multiple id={`task-files-${taskId}`}
            aria-label={t("tasks.selectMedia")} disabled={busy}
            onChange={(event) => setFiles(Array.from(event.target.files || []))} />
          <Button color="primary" disabled={busy || !files.length} onClick={upload}>{t("uploadMedia")}</Button>
        </div>}
        <Dialog open={!!deleting} onClose={() => { if (!busy) setDeleting(null); }} aria-labelledby="task-delete-file-title">
          <DialogTitle id="task-delete-file-title">{t("tasks.deleteMedia")}</DialogTitle>
          <DialogContent><Typography>{t("tasks.deleteMediaConfirmation", { filename: deleting })}</Typography></DialogContent>
          <DialogActions>
            <Button disabled={busy} onClick={() => setDeleting(null)}>{t("tasks.cancel")}</Button>
            <Button disabled={busy} color="secondary" onClick={remove}>{t("tasks.deleteMedia")}</Button>
          </DialogActions>
        </Dialog>
      </>}
    </section>
  );
}
